/**
 * Social Features Database References
 *
 * Social collections:
 * - /clients/{clientKey}/follows
 * - /clients/{clientKey}/likes
 * - /clients/{clientKey}/comments
 * - /clients/{clientKey}/user_coins
 * - /clients/{clientKey}/recruiter_verification_requests
 */

import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase';

/* ═══════════════════════════════════════════════════════════
   FOLLOWS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the follows collection
 * Path: /clients/{clientKey}/follows
 */
export const FollowsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'follows');
};

/**
 * Reference to a specific follow document
 * Path: /clients/{clientKey}/follows/{followId}
 */
export const FollowRef_Document = (
  followId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'follows', followId);
};

/**
 * Query for users that a specific user is following
 */
export const UserFollowingRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    FollowsRef_Collection(clientKey),
    where('follower_id', '==', userId),
    orderBy('followed_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for users following a specific user
 */
export const UserFollowersRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    FollowsRef_Collection(clientKey),
    where('followed_id', '==', userId),
    orderBy('followed_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   LIKES COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the likes collection
 * Path: /clients/{clientKey}/likes
 */
export const LikesRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'likes');
};

/**
 * Reference to a specific like document
 * Path: /clients/{clientKey}/likes/{likeId}
 */
export const LikeRef_Document = (
  likeId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'likes', likeId);
};

/**
 * Query for likes on a session
 */
export const SessionLikesRef_Query = (
  sessionId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    LikesRef_Collection(clientKey),
    where('session_id', '==', sessionId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for likes by a user
 */
export const UserLikesRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    LikesRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   COMMENTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the comments collection
 * Path: /clients/{clientKey}/comments
 */
export const CommentsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'comments');
};

/**
 * Reference to a specific comment document
 * Path: /clients/{clientKey}/comments/{commentId}
 */
export const CommentRef_Document = (
  commentId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'comments', commentId);
};

/**
 * Query for comments on a session
 */
export const SessionCommentsRef_Query = (
  sessionId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    CommentsRef_Collection(clientKey),
    where('session_id', '==', sessionId),
    orderBy('created_at', 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for comments by a user
 */
export const UserCommentsRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    CommentsRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   USER COINS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the user coins collection
 * Path: /clients/{clientKey}/user_coins
 */
export const UserCoinsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'user_coins');
};

/**
 * Reference to a specific user coin document
 * Path: /clients/{clientKey}/user_coins/{coinId}
 */
export const UserCoinRef_Document = (
  coinId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'user_coins', coinId);
};

/**
 * Query for coins by user
 */
export const UserCoinsRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UserCoinsRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('earned_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for user's coin balance (aggregate)
 * Note: This requires client-side aggregation
 */
export const UserCoinBalanceRef_Query = (
  userId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    UserCoinsRef_Collection(clientKey),
    where('user_id', '==', userId)
  );
};

/* ═══════════════════════════════════════════════════════════
   RECRUITER VERIFICATION REQUESTS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the recruiter verification requests collection
 * Path: /clients/{clientKey}/recruiter_verification_requests
 */
export const RecruiterVerificationRequestsRef_Collection = (
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'recruiter_verification_requests');
};

/**
 * Reference to a specific recruiter verification request document
 * Path: /clients/{clientKey}/recruiter_verification_requests/{requestId}
 */
export const RecruiterVerificationRequestRef_Document = (
  requestId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'recruiter_verification_requests', requestId);
};

/**
 * Query for recruiter verification requests by status
 */
export const RecruiterVerificationRequestsByStatusRef_Query = (
  status: 'pending' | 'approved' | 'rejected',
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    RecruiterVerificationRequestsRef_Collection(clientKey),
    where('status', '==', status),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for recruiter verification requests by user
 */
export const RecruiterVerificationRequestsByUserRef_Query = (
  userId: string,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    RecruiterVerificationRequestsRef_Collection(clientKey),
    where('user_id', '==', userId),
    orderBy('created_at', 'desc')
  );
};

/**
 * Query for all recruiter verification requests ordered by created date
 */
export const RecruiterVerificationRequestsOrderedRef_Query = (
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    RecruiterVerificationRequestsRef_Collection(clientKey),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};
